'use client';

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { CategoryChangeAction } from '@/hooks/useCategoryChange';
import {
  UsersIcon,
  Phone,
  Mail,
  Calendar,
  Cake,
  Repeat,
  BadgeCheck,
  CircleDollarSign,
  Undo2,
  ArrowUpDown,
} from 'lucide-react';
import type { Player } from '@/types';
import type { Gender } from '@/types';
import { getGenderLabel } from '@/utils/gender-utils';
import {
  formatBirthdateForDisplay,
  getAgeFromBirthdateIso,
} from '@/utils/formatters';
import { EloCategory } from '../../models/elo.model';
import { ELO_RATING_BADGE_CLASSNAME, formatEloBadgeLabel } from '../../utils';

interface PlayerCardProps {
  player: Player;
  playerIndex: number;
  sportUid: string;
  eloCategories?: EloCategory[];
  getPlayerInitials: (name: string) => string;
  formatWhatsAppNumber: (phone: string) => string;
  /** If present, shows the "Reemplazar" button for this player. */
  onReplace?: () => void;
  /**
   * Per-player payment state. Only meaningful on INDIVIDUAL registrations, where each player pays
   * their own half; leave it out everywhere else and no payment UI is rendered.
   */
  hasPaid?: boolean;
  /** If present, offers to record a payment this player made outside the app. */
  onMarkPaid?: () => void;
  /** If present, offers to undo a payment the manager recorded by hand for this player. */
  onUndoPayment?: () => void;
  /** If present, shows the "Cambiar categoría" button; blocked when not eligible. */
  categoryChange?: CategoryChangeAction;
}

export function PlayerCard({
  player,
  playerIndex,
  sportUid,
  eloCategories = [],
  getPlayerInitials,
  formatWhatsAppNumber,
  onReplace,
  hasPaid,
  onMarkPaid,
  onUndoPayment,
  categoryChange,
}: PlayerCardProps) {
  const [showBlockedReason, setShowBlockedReason] = useState(false);
  // Find ranking for the current sport
  const sportRanking = player.rankings?.find((r) => r.sport?.uid === sportUid);

  // Create an array of additional information elements
  const playerInfoItems = [];

  if (player.gender) {
    playerInfoItems.push(
      <span key="gender" className="flex items-center gap-1">
        <UsersIcon className="h-3 w-3" />
        {getGenderLabel(player.gender as Gender)}
      </span>,
    );
  }

  if (player.cellphone) {
    playerInfoItems.push(
      <span key="phone" className="flex items-center gap-1">
        <Phone className="h-3 w-3" />
        {player.cellphone}
      </span>,
    );
  }

  const birthLabel = formatBirthdateForDisplay(player.birthdate);
  const age = getAgeFromBirthdateIso(player.birthdate);
  if (birthLabel) {
    playerInfoItems.push(
      <span key="birthdate" className="flex items-center gap-1">
        <Calendar className="h-3 w-3 shrink-0" />
        <span>Nac. {birthLabel}</span>
      </span>,
    );
  }
  if (age != null) {
    playerInfoItems.push(
      <span key="age" className="flex items-center gap-1">
        <Cake className="h-3 w-3 shrink-0" />
        <span>{age} años</span>
      </span>,
    );
  }

  // Get player initials
  const initials = getPlayerInitials(player.name);

  const hasBadges = !!sportRanking || hasPaid !== undefined;

  // minmax(0,1fr): a long email can't widen the card past its container. content-start: when a taller
  // neighbour stretches the card, the spare height stays at the bottom, not between the rows. The
  // `contents` wrappers let the badges join the name's line from sm up yet get a full-width row on a
  // phone, where the column beside the avatar is too narrow for them.
  return (
    <div
      key={player.id || `player-${playerIndex}`}
      className="grid grid-cols-[auto_minmax(0,1fr)] content-start items-start gap-x-3 gap-y-1.5 rounded-lg border p-3 transition-colors hover:bg-gray-50"
    >
      {player.profileImageUrl ? (
        <img
          src={player.profileImageUrl}
          alt={player.name}
          className="h-12 w-12 rounded-full object-cover sm:row-span-2"
          onError={(e) => {
            // Fallback to initials if image fails to load
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
      ) : null}
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary sm:row-span-2 ${player.profileImageUrl ? 'hidden' : ''}`}
      >
        {initials}
      </div>
      <div className="contents flex-wrap items-center gap-1.5 sm:flex">
        <p className="min-w-0 self-center break-words text-base font-semibold text-gray-800 sm:text-lg">
          {player.name}
        </p>
        {hasBadges && (
          <div className="col-span-full flex flex-wrap items-center gap-1.5 sm:contents">
            {sportRanking && (
              <Badge className={ELO_RATING_BADGE_CLASSNAME}>
                {formatEloBadgeLabel(sportRanking.elo, eloCategories)}
              </Badge>
            )}
            {hasPaid !== undefined &&
              (hasPaid ? (
                <Badge className="shrink-0 gap-1 bg-green-100 text-green-800 hover:bg-green-100">
                  <BadgeCheck className="h-3 w-3" />
                  Pagó
                </Badge>
              ) : (
                <Badge className="shrink-0 gap-1 bg-amber-100 text-amber-800 hover:bg-amber-100">
                  Pago pendiente
                </Badge>
              ))}
          </div>
        )}
      </div>
      <div className="col-span-full space-y-1.5 sm:col-start-2">
        <p className="break-words text-sm text-gray-500">{player.email}</p>

        {playerInfoItems.length > 0 && (
          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            {playerInfoItems}
          </div>
        )}

        {/* Contact options */}
        <div className="flex flex-wrap gap-2 pt-1">
          {player.cellphone && (
            <>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full"
                asChild
                title="WhatsApp"
              >
                <a
                  href={`https://wa.me/${formatWhatsAppNumber(player.cellphone)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center"
                >
                  <img
                    src="/assets/icons/whatsapp-icon.svg"
                    alt="WhatsApp"
                    className="h-4 w-4"
                    style={{
                      filter:
                        'invert(42%) sepia(93%) saturate(1352%) hue-rotate(87deg) brightness(119%) contrast(119%)',
                    }}
                  />
                </a>
              </Button>

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-full"
                asChild
                title="Llamar"
              >
                <a href={`tel:${player.cellphone}`}>
                  <Phone className="h-4 w-4 text-blue-600" />
                </a>
              </Button>
            </>
          )}

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full"
            asChild
            title="Email"
          >
            <a
              href={`mailto:${player.email}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Mail className="h-4 w-4 text-gray-600" />
            </a>
          </Button>

          {onReplace && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1 rounded-full"
              onClick={onReplace}
              title="Reemplazar jugador"
            >
              <Repeat className="h-4 w-4 text-gray-600" />
              <span className="text-xs">Reemplazar</span>
            </Button>
          )}

          {categoryChange && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1 rounded-full"
              // Not `disabled`: a blocked button still has to be tappable to explain why.
              aria-disabled={categoryChange.eligible ? undefined : true}
              onClick={
                categoryChange.eligible
                  ? categoryChange.onOpen
                  : () => setShowBlockedReason((shown) => !shown)
              }
              title="Cambiar categoría"
            >
              <ArrowUpDown className="h-4 w-4 text-gray-600" />
              <span className="text-xs">Cambiar categoría</span>
            </Button>
          )}

          {onMarkPaid && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1 rounded-full"
              onClick={onMarkPaid}
              title="Registrar el pago que este jugador hizo por fuera de la app"
            >
              <CircleDollarSign className="h-4 w-4 text-green-600" />
              <span className="text-xs">Marcar como pagado</span>
            </Button>
          )}

          {onUndoPayment && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 gap-1 rounded-full"
              onClick={onUndoPayment}
              title="Deshacer el pago que registraste a mano"
            >
              <Undo2 className="h-4 w-4 text-gray-600" />
              <span className="text-xs">Deshacer pago</span>
            </Button>
          )}
        </div>

        {showBlockedReason && categoryChange?.blockedMessage && (
          <p
            role="status"
            className="rounded-md border bg-gray-50 px-2 py-1.5 text-xs text-gray-600"
          >
            {categoryChange.blockedMessage}
          </p>
        )}
      </div>
    </div>
  );
}
